import {useEffect, useState} from "react";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import {PrimaryButton} from "../../../../../components/general/PrimaryButton.tsx";
import BusinessRegistrationFormFields from "../../../../../components/business/BusinessRegistrationFormFields.tsx";
import {useNotifications} from "../../../../../lib/NotificationContext.tsx";
import {
    BusinessRegistrationFormData,
    CreateBusinessApiPayload,
    EMPTY_BUSINESS_REGISTRATION_FORM,
    toCreateBusinessApiPayload,
    validateBusinessRegistrationForm,
} from "../../../../../lib/businessRegistration.ts";

interface AddBusinessModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    createNewBusiness: (data: CreateBusinessApiPayload) => Promise<void>;
}

export default function AddBusinessPopup({open, onClose, onSuccess, createNewBusiness}: AddBusinessModalProps) {
    const {dispatch} = useNotifications();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<BusinessRegistrationFormData>(EMPTY_BUSINESS_REGISTRATION_FORM);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (open) {
            setFormData(EMPTY_BUSINESS_REGISTRATION_FORM);
            setErrors({});
        }
    }, [open]);

    const updateField = <K extends keyof BusinessRegistrationFormData>(
        field: K,
        value: BusinessRegistrationFormData[K],
    ) => {
        setFormData((prev) => ({...prev, [field]: value}));
        if (errors[field]) {
            setErrors((prev) => ({...prev, [field]: ""}));
        }
    };

    const handleSubmit = async (event?: React.FormEvent) => {
        event?.preventDefault();

        const validationErrors = validateBusinessRegistrationForm(formData);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            return;
        }

        setLoading(true);
        try {
            await createNewBusiness(toCreateBusinessApiPayload(formData));
            onSuccess();
        } catch {
            dispatch({
                title: "Fout opgetreden",
                description: "Bedrijf toevoegen mislukt. Probeer het opnieuw.",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={loading ? () => {} : onClose} className="relative z-40">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative transform overflow-visible rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                    >
                        <div>
                            <DialogTitle as="h3" className="text-base font-semibold text-gray-900 mb-4">
                                Nieuw bedrijf toevoegen
                            </DialogTitle>

                            <form
                                onSubmit={(event) => {
                                    void handleSubmit(event);
                                }}
                                className="space-y-4"
                            >
                                <BusinessRegistrationFormFields
                                    formData={formData}
                                    errors={errors}
                                    loading={loading}
                                    onFieldChange={updateField}
                                    className="space-y-4"
                                />
                            </form>
                        </div>

                        <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                            <PrimaryButton
                                title={loading ? "Toevoegen..." : "Bedrijf toevoegen"}
                                onClick={() => void handleSubmit()}
                                disabled={loading}
                                className="inline-flex w-full justify-center sm:col-start-2"
                            />
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={loading}
                                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Annuleren
                            </button>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
